import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  ArrowLeft,
  User,
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Edit3,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Tag,
  Zap,
  FileText,
  DollarSign,
  Shield,
  Bell,
  BellOff,
  ExternalLink,
  MoreVertical,
  Star,
  StarOff,
  Copy,
  Check,
  Send,
  Paperclip,
  Image,
  Smile,
  Link2,
  MessageSquare,
  Globe,
  Activity,
  UserPlus,
  Settings,
  Briefcase,
  Hash,
  AtSign,
  CreditCard,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { cn } from '../../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel type configurations
const CHANNEL_TYPES = {
  portal: { label: 'Portal', icon: Globe, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  sms: { label: 'SMS', icon: MessageSquare, color: 'bg-green-100 text-green-700 border-green-200' },
  email: { label: 'Email', icon: Mail, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  internal: { label: 'Internal Note', icon: FileText, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  call: { label: 'Call', icon: Phone, color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

// Activity type icons
const ACTIVITY_ICONS = {
  message: MessageSquare,
  email: Mail,
  sms: MessageSquare,
  call: Phone,
  document: FileText,
  automation: Zap,
  tag: Tag,
  created: UserPlus,
  status: Activity,
  payment: DollarSign,
  portal: Globe,
  dispute: AlertCircle,
  case: Briefcase,
};

// Mock contact data
const MOCK_CONTACT = {
  id: 'contact_001',
  firstName: 'John',
  lastName: 'Smith',
  email: 'john.smith@email.com',
  phone: '+1 (555) 123-4567',
  phoneRaw: '+15551234567',
  dateOfBirth: '1985-03-15',
  source: 'Portal Signup',
  contactType: 'Client',
  status: 'active',
  company: {
    name: 'Smith Enterprises',
    phone: '+1 (555) 999-8888',
    email: 'contact@smithent.com',
    website: 'https://smithenterprises.com',
    address: '456 Business Blvd, Suite 200, Los Angeles, CA 90001',
  },
  address: {
    street: '123 Main Street',
    city: 'Los Angeles',
    state: 'CA',
    zip: '90001',
  },
  ssn_last4: '4567',
  tags: ['VIP', 'Active Dispute', 'High Priority'],
  owner: {
    id: 'user_001',
    name: 'Sarah Wilson',
    avatar: null,
  },
  followers: [
    { id: 'user_002', name: 'Mike Brown', avatar: null },
    { id: 'user_003', name: 'Jane Doe', avatar: null },
  ],
  dnd: {
    all: false,
    emails: false,
    sms: false,
    calls: true,
    voicemails: false,
    incomingDnd: false,
  },
  automations: [
    { id: 'auto_001', name: 'Welcome Sequence', status: 'active', step: 3, totalSteps: 5 },
    { id: 'auto_002', name: 'Document Reminder', status: 'paused', step: 1, totalSteps: 3 },
  ],
  cases: [
    { id: 'case_001', name: 'Credit Repair - Full Service', status: 'active', progress: 45 },
  ],
  createdAt: '2024-12-01T10:30:00Z',
  createdBy: 'Portal Signup Form',
  lastActivity: '2024-12-14T15:45:00Z',
};

// Mock messages
const MOCK_MESSAGES = [
  {
    id: 'msg_001',
    content: 'Hello! I just signed up for your credit repair service. What are the next steps?',
    channel: 'portal',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
    sender: 'contact',
    senderName: 'John Smith',
  },
  {
    id: 'msg_002',
    content: 'Welcome to FundElite Credit Repair! We\'re excited to help you improve your credit score. First, we need you to upload your credit report.',
    channel: 'portal',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 23),
    sender: 'agent',
    senderName: 'Sarah Wilson',
  },
  {
    id: 'msg_003',
    content: 'I\'ve uploaded my TransUnion report. Should I also upload Experian and Equifax?',
    channel: 'portal',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 20),
    sender: 'contact',
    senderName: 'John Smith',
  },
  {
    id: 'msg_004',
    content: 'Client requested expedited processing - marking as VIP.',
    channel: 'internal',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 18),
    sender: 'agent',
    senderName: 'Sarah Wilson',
  },
  {
    id: 'msg_005',
    content: 'Just a reminder about your documents!',
    channel: 'sms',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8),
    sender: 'agent',
    senderName: 'Auto-Reminder',
  },
  {
    id: 'msg_006',
    content: 'Thank you for the update on my dispute status. When can I expect the next round?',
    channel: 'portal',
    timestamp: new Date(Date.now() - 1000 * 60 * 5),
    sender: 'contact',
    senderName: 'John Smith',
  },
];

// Mock activity timeline
const MOCK_ACTIVITIES = [
  { id: 1, type: 'message', channel: 'portal', text: 'Sent message via Portal', time: '5 min ago', timestamp: new Date() },
  { id: 2, type: 'automation', text: 'Entered "Welcome Sequence" step 3', time: '2 hours ago', timestamp: new Date() },
  { id: 3, type: 'document', text: 'Uploaded TransUnion credit report', time: '1 day ago', timestamp: new Date() },
  { id: 4, type: 'email', text: 'Email opened: "Welcome to FundElite"', time: '1 day ago', timestamp: new Date() },
  { id: 5, type: 'portal', text: 'Logged into client portal', time: '2 days ago', timestamp: new Date() },
  { id: 6, type: 'tag', text: 'Tagged as "VIP"', time: '2 days ago', timestamp: new Date() },
  { id: 7, type: 'case', text: 'Case created: Credit Repair - Full Service', time: '3 days ago', timestamp: new Date() },
  { id: 8, type: 'payment', text: 'Payment received: $299.00', time: '3 days ago', timestamp: new Date() },
  { id: 9, type: 'created', text: 'Contact created from Portal signup', time: '3 days ago', timestamp: new Date() },
];

// Channel Badge Component
const ChannelBadge = ({ channel, size = 'sm' }) => {
  const config = CHANNEL_TYPES[channel] || CHANNEL_TYPES.portal;
  const Icon = config.icon;
  
  return (
    <Badge 
      variant="outline" 
      className={cn(
        'font-medium',
        config.color,
        size === 'sm' ? 'text-xs px-1.5 py-0' : 'text-xs px-2 py-0.5'
      )}
    >
      <Icon size={size === 'sm' ? 10 : 12} className="mr-1" />
      {config.label}
    </Badge>
  );
};

// Message Bubble Component
const MessageBubble = ({ message }) => {
  const isAgent = message.sender === 'agent';
  
  const formatTimestamp = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) +
      ' · ' + date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className={cn('flex gap-3 mb-4', isAgent && 'flex-row-reverse')}>
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarFallback className={cn(
          'text-xs',
          isAgent ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-700'
        )}>
          {message.senderName.split(' ').map(n => n[0]).join('')}
        </AvatarFallback>
      </Avatar>
      
      <div className={cn('max-w-[70%]', isAgent && 'items-end')}>
        <div className={cn('flex items-center gap-2 mb-1', isAgent && 'flex-row-reverse')}>
          <span className="text-xs font-medium text-slate-700">{message.senderName}</span>
          <ChannelBadge channel={message.channel} />
        </div>
        
        <div className={cn(
          'rounded-2xl px-4 py-2.5',
          isAgent 
            ? 'bg-teal-500 text-white rounded-tr-sm' 
            : 'bg-slate-100 text-slate-800 rounded-tl-sm',
          message.channel === 'internal' && 'bg-amber-50 text-amber-900 border border-amber-200'
        )}>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
        </div>
        
        <p className={cn('text-xs text-slate-400 mt-1', isAgent && 'text-right')}>
          {formatTimestamp(message.timestamp)}
        </p>
      </div>
    </div>
  );
};

// Editable Field Component
const EditableField = ({ label, value, onChange, type = 'text', placeholder, disabled }) => {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="h-9 text-sm"
      />
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-slate-500" />}
          <span className="text-sm font-medium text-slate-700">{title}</span>
        </div>
        <ChevronRight 
          size={14} 
          className={cn('text-slate-400 transition-transform', isOpen && 'rotate-90')} 
        />
      </button>
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

// Activity Timeline Item
const ActivityItem = ({ activity }) => {
  const IconComponent = ACTIVITY_ICONS[activity.type] || Activity;
  
  return (
    <div className="flex items-start gap-3 py-2">
      <div className={cn(
        'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
        activity.type === 'payment' ? 'bg-emerald-100' :
        activity.type === 'automation' ? 'bg-purple-100' :
        activity.type === 'created' ? 'bg-blue-100' :
        'bg-slate-100'
      )}>
        <IconComponent size={12} className={cn(
          activity.type === 'payment' ? 'text-emerald-600' :
          activity.type === 'automation' ? 'text-purple-600' :
          activity.type === 'created' ? 'text-blue-600' :
          'text-slate-500'
        )} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm text-slate-700">{activity.text}</p>
          {activity.channel && <ChannelBadge channel={activity.channel} />}
        </div>
        <p className="text-xs text-slate-400">{activity.time}</p>
      </div>
    </div>
  );
};

// Main Contact Workspace Page
const ContactWorkspacePage = () => {
  const { agencyId, contactId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const messageEndRef = useRef(null);

  // State
  const [contact, setContact] = useState(MOCK_CONTACT);
  const [messages, setMessages] = useState(MOCK_MESSAGES);
  const [activities, setActivities] = useState(MOCK_ACTIVITIES);
  const [messageInput, setMessageInput] = useState('');
  const [activeChannel, setActiveChannel] = useState('portal');
  const [activeTab, setActiveTab] = useState('contact'); // 'contact' | 'company'
  const [isEditing, setIsEditing] = useState(false);
  const [editedContact, setEditedContact] = useState({});
  const [hideEmpty, setHideEmpty] = useState(false);

  // Initialize edited contact
  useEffect(() => {
    setEditedContact({ ...contact });
  }, [contact]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;

    const newMessage = {
      id: `msg_${Date.now()}`,
      content: messageInput,
      channel: activeChannel,
      timestamp: new Date(),
      sender: 'agent',
      senderName: 'You',
    };

    setMessages(prev => [...prev, newMessage]);
    setMessageInput('');
  };

  const handleSaveContact = () => {
    setContact(editedContact);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedContact({ ...contact });
    setIsEditing(false);
  };

  const handleToggleDnd = (field) => {
    setEditedContact(prev => ({
      ...prev,
      dnd: {
        ...prev.dnd,
        [field]: !prev.dnd[field],
      },
    }));
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditedContact(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove),
    }));
  };

  const fullName = `${contact.firstName} ${contact.lastName}`;
  const initials = fullName.split(' ').map(n => n[0]).join('');

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-white" data-testid="contact-workspace-page">
      {/* Top Header */}
      <div className="px-6 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/agency/${agencyId}/inbox`)}
              className="gap-2"
            >
              <ArrowLeft size={16} />
              Back to Inbox
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-800 text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-lg font-bold text-slate-900" data-testid="contact-workspace-header">
                  Contact Workspace
                </h1>
                <p className="text-sm text-slate-500">{fullName}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Owner */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Owner:</span>
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-xs bg-teal-100 text-teal-700">
                  {contact.owner.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-slate-700">{contact.owner.name}</span>
            </div>
            
            <div className="h-6 w-px bg-slate-200" />
            
            {/* Followers */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-500">Followers:</span>
              {contact.followers.slice(0, 3).map((follower, i) => (
                <Avatar key={follower.id} className={cn('h-6 w-6', i > 0 && '-ml-1')}>
                  <AvatarFallback className="text-xs bg-slate-100 text-slate-600">
                    {follower.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
              ))}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full">
                <Plus size={12} className="text-slate-400" />
              </Button>
            </div>
            
            <div className="h-6 w-px bg-slate-200" />
            
            {/* Quick Actions */}
            <Button variant="outline" size="sm" className="gap-2">
              <Phone size={14} />
              Call
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Mail size={14} />
              Email
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical size={16} className="text-slate-400" />
            </Button>
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Column - Contact Record / Editor */}
        <div className="w-96 border-r border-slate-200 flex flex-col bg-white overflow-y-auto">
          {/* Contact/Company Tabs */}
          <div className="flex border-b border-slate-200 sticky top-0 bg-white z-10">
            <button
              onClick={() => setActiveTab('contact')}
              className={cn(
                'flex-1 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'contact'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              )}
            >
              <User size={14} className="inline mr-2" />
              Contact
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={cn(
                'flex-1 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'company'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              )}
            >
              <Building2 size={14} className="inline mr-2" />
              Company
            </button>
          </div>

          {/* Edit/Save Controls */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
            <label className="flex items-center gap-2 text-xs text-slate-500">
              <input
                type="checkbox"
                checked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)}
                className="rounded border-slate-300"
              />
              Hide empty fields
            </label>
            {isEditing ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSaveContact} className="bg-teal-600 hover:bg-teal-700">
                  <Save size={12} className="mr-1" />
                  Save
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                <Edit3 size={12} className="mr-1" />
                Edit
              </Button>
            )}
          </div>

          {activeTab === 'contact' ? (
            <div className="flex-1">
              {/* General Info */}
              <CollapsibleSection title="General Info" icon={User} defaultOpen={true}>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <EditableField
                      label="First Name"
                      value={editedContact.firstName}
                      onChange={(v) => setEditedContact(p => ({ ...p, firstName: v }))}
                      disabled={!isEditing}
                    />
                    <EditableField
                      label="Last Name"
                      value={editedContact.lastName}
                      onChange={(v) => setEditedContact(p => ({ ...p, lastName: v }))}
                      disabled={!isEditing}
                    />
                  </div>
                  <EditableField
                    label="Email"
                    value={editedContact.email}
                    onChange={(v) => setEditedContact(p => ({ ...p, email: v }))}
                    type="email"
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Phone"
                    value={editedContact.phone}
                    onChange={(v) => setEditedContact(p => ({ ...p, phone: v }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Date of Birth"
                    value={editedContact.dateOfBirth}
                    onChange={(v) => setEditedContact(p => ({ ...p, dateOfBirth: v }))}
                    type="date"
                    disabled={!isEditing}
                  />
                </div>
              </CollapsibleSection>

              {/* Additional Info */}
              <CollapsibleSection title="Additional Info" icon={FileText} defaultOpen={false}>
                <div className="space-y-3">
                  <EditableField
                    label="Contact Source"
                    value={editedContact.source}
                    onChange={(v) => setEditedContact(p => ({ ...p, source: v }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Contact Type"
                    value={editedContact.contactType}
                    onChange={(v) => setEditedContact(p => ({ ...p, contactType: v }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Street Address"
                    value={editedContact.address?.street}
                    onChange={(v) => setEditedContact(p => ({ ...p, address: { ...p.address, street: v } }))}
                    disabled={!isEditing}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <EditableField
                      label="City"
                      value={editedContact.address?.city}
                      onChange={(v) => setEditedContact(p => ({ ...p, address: { ...p.address, city: v } }))}
                      disabled={!isEditing}
                    />
                    <EditableField
                      label="State"
                      value={editedContact.address?.state}
                      onChange={(v) => setEditedContact(p => ({ ...p, address: { ...p.address, state: v } }))}
                      disabled={!isEditing}
                    />
                    <EditableField
                      label="ZIP"
                      value={editedContact.address?.zip}
                      onChange={(v) => setEditedContact(p => ({ ...p, address: { ...p.address, zip: v } }))}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </CollapsibleSection>

              {/* Tags */}
              <CollapsibleSection title="Tags" icon={Tag} defaultOpen={true}>
                <div className="flex flex-wrap gap-1.5">
                  {editedContact.tags?.map((tag) => (
                    <Badge key={tag} variant="outline" className="bg-slate-50 text-slate-700 pr-1">
                      {tag}
                      {isEditing && (
                        <button 
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 p-0.5 hover:bg-slate-200 rounded"
                        >
                          <X size={10} />
                        </button>
                      )}
                    </Badge>
                  ))}
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-500">
                    <Plus size={10} className="mr-1" />
                    Add
                  </Button>
                </div>
              </CollapsibleSection>

              {/* Active Automations */}
              <CollapsibleSection title="Active Automations" icon={Zap} defaultOpen={true}>
                <div className="space-y-2">
                  {contact.automations.map((auto) => (
                    <div 
                      key={auto.id}
                      className={cn(
                        'p-2.5 rounded-lg border',
                        auto.status === 'active' 
                          ? 'bg-purple-50 border-purple-200' 
                          : 'bg-slate-50 border-slate-200'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap size={12} className={auto.status === 'active' ? 'text-purple-500' : 'text-slate-400'} />
                          <span className="text-sm font-medium text-slate-700">{auto.name}</span>
                        </div>
                        <Badge variant="outline" className={cn(
                          'text-xs',
                          auto.status === 'active' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                        )}>
                          {auto.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Step {auto.step} of {auto.totalSteps}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>

              {/* Cases / Service Status */}
              <CollapsibleSection title="Cases / Service Status" icon={Briefcase} defaultOpen={true}>
                <div className="space-y-2">
                  {contact.cases.map((caseItem) => (
                    <div key={caseItem.id} className="p-2.5 bg-teal-50 rounded-lg border border-teal-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-teal-900">{caseItem.name}</span>
                        <Badge variant="outline" className="bg-teal-100 text-teal-700 text-xs">
                          {caseItem.status}
                        </Badge>
                      </div>
                      <div className="w-full h-1.5 bg-teal-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-teal-500 rounded-full"
                          style={{ width: `${caseItem.progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-teal-600 mt-1">{caseItem.progress}% complete</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>

              {/* DND Settings */}
              <CollapsibleSection title="Do Not Disturb" icon={BellOff} defaultOpen={false}>
                <div className="space-y-2">
                  {[
                    { key: 'all', label: 'DND All Channels' },
                    { key: 'emails', label: 'Emails' },
                    { key: 'sms', label: 'Text Messages' },
                    { key: 'calls', label: 'Calls & Voicemails' },
                    { key: 'incomingDnd', label: 'Incoming DND' },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between py-1">
                      <span className="text-sm text-slate-600">{item.label}</span>
                      <button
                        onClick={() => isEditing && handleToggleDnd(item.key)}
                        className={cn(
                          'w-10 h-5 rounded-full transition-colors relative',
                          editedContact.dnd?.[item.key] ? 'bg-red-500' : 'bg-slate-200',
                          !isEditing && 'opacity-60 cursor-not-allowed'
                        )}
                      >
                        <div className={cn(
                          'absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform',
                          editedContact.dnd?.[item.key] ? 'translate-x-5' : 'translate-x-0.5'
                        )} />
                      </button>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>

              {/* Source / Creation Data */}
              <CollapsibleSection title="Source & Metadata" icon={Activity} defaultOpen={false}>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Created By</span>
                    <span className="text-slate-700">{contact.createdBy}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Source</span>
                    <span className="text-slate-700">{contact.source}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Created At</span>
                    <span className="text-slate-700">{new Date(contact.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last Activity</span>
                    <span className="text-slate-700">{new Date(contact.lastActivity).toLocaleDateString()}</span>
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-2 text-xs">
                    View Audit Log
                  </Button>
                </div>
              </CollapsibleSection>
            </div>
          ) : (
            <div className="flex-1">
              {/* Company Info */}
              <CollapsibleSection title="Company Details" icon={Building2} defaultOpen={true}>
                <div className="space-y-3">
                  <EditableField
                    label="Company Name"
                    value={editedContact.company?.name}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, name: v } }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Phone"
                    value={editedContact.company?.phone}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, phone: v } }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Email"
                    value={editedContact.company?.email}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, email: v } }))}
                    type="email"
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Website"
                    value={editedContact.company?.website}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, website: v } }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Address"
                    value={editedContact.company?.address}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, address: v } }))}
                    disabled={!isEditing}
                  />
                </div>
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Center Column - Conversation Thread */}
        <div className="flex-1 flex flex-col min-w-0 border-r border-slate-200">
          {/* Thread Header */}
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-testid="conversation-thread-label">
              Conversation Thread
            </h2>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 bg-white">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            <div ref={messageEndRef} />
          </div>

          {/* Composer */}
          <div className="border-t border-slate-200 p-3 bg-slate-50">
            {/* Channel Tabs */}
            <div className="flex items-center gap-1 mb-3">
              {Object.entries(CHANNEL_TYPES).filter(([key]) => !['call'].includes(key)).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveChannel(key)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                      activeChannel === key
                        ? 'bg-teal-100 text-teal-700'
                        : 'text-slate-500 hover:bg-slate-100'
                    )}
                  >
                    <Icon size={12} />
                    {config.label}
                  </button>
                );
              })}
            </div>

            {/* Input */}
            <div className="flex items-end gap-2">
              <div className="flex-1 bg-white rounded-lg border border-slate-200 focus-within:border-teal-400 focus-within:ring-1 focus-within:ring-teal-400">
                <textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder={`Type your ${CHANNEL_TYPES[activeChannel].label.toLowerCase()} message...`}
                  className="w-full px-3 py-2 text-sm resize-none focus:outline-none rounded-lg min-h-[60px] max-h-[120px]"
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <div className="flex items-center justify-between px-2 pb-2">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Paperclip size={14} className="text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Image size={14} className="text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Smile size={14} className="text-slate-400" />
                    </Button>
                  </div>
                  {activeChannel === 'sms' && messageInput.length > 0 && (
                    <span className="text-xs text-slate-400">
                      {messageInput.length}/160 · {Math.ceil(messageInput.length / 160)} segment
                    </span>
                  )}
                </div>
              </div>
              <Button 
                onClick={handleSendMessage}
                disabled={!messageInput.trim()}
                className="h-10 px-4 bg-teal-600 hover:bg-teal-700"
              >
                <Send size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Activity Timeline */}
        <div className="w-80 bg-white flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-testid="activity-timeline-label">
              Activity Timeline
            </h2>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {activities.map((activity) => (
                <ActivityItem key={activity.id} activity={activity} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactWorkspacePage;
